import { useEffect, useState } from 'react'
import { Users, Copy, Share2, Gift, UserPlus, Check, ArrowLeft, ExternalLink } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useReferralStore } from '../store/referralStore'

export default function Partner() {
  const navigate = useNavigate()
  const { info, rewards, fetchInfo, fetchRewards, generateCode, isLoading } = useReferralStore()
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    fetchInfo()
    fetchRewards()
  }, [fetchInfo, fetchRewards])

  const handleCopy = () => {
    if (!info?.code) return
    navigator.clipboard.writeText(info.referral_link).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  const handleGenerate = () => {
    generateCode().then(() => fetchRewards())
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <button
          onClick={() => navigate('/profile')}
          className="p-2 rounded-xl glass-hover text-gray-400 hover:text-white transition-all"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-2xl font-bold gradient-text flex items-center gap-2">
          <Users className="w-6 h-6" />
          Реферальная программа
        </h1>
      </div>

      {/* Hero */}
      <div className="glass-card p-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/5 rounded-full -translate-y-32 translate-x-32" />
        <div className="relative z-10">
          <h2 className="text-lg font-semibold text-white">Приглашайте друзей — получайте бонусы!</h2>
          <p className="text-sm text-gray-400 mt-1">
            За каждого приглашённого пользователя вы получаете <span className="text-amber-300 font-semibold">500 токенов</span>, 
            а ваш друг — <span className="text-amber-300 font-semibold">200 токенов</span> в подарок.
          </p>
          <div className="flex flex-wrap gap-3 mt-4">
            {info?.code ? (
              <div className="flex items-center gap-2">
                <div className="px-4 py-2 rounded-xl bg-white/5 border border-white/10 font-mono text-sm text-amber-300">
                  {info.code}
                </div>
                <button
                  onClick={handleCopy}
                  className="p-2 rounded-xl glass-hover text-gray-400 hover:text-amber-300 transition-all"
                  title="Копировать ссылку"
                >
                  {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            ) : (
              <button
                onClick={handleGenerate}
                disabled={isLoading}
                className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-semibold hover:from-amber-400 hover:to-orange-400 disabled:opacity-50 transition-all"
              >
                {isLoading ? 'Генерация...' : 'Создать реферальный код'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Stats */}
      {info && info.code && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="glass-card p-5 text-center">
            <div className="p-2 rounded-lg bg-amber-500/10 inline-flex mb-3">
              <Users className="w-5 h-5 text-amber-400" />
            </div>
            <p className="text-3xl font-bold text-white">{info.total_referred}</p>
            <p className="text-xs text-gray-500 mt-1">Приглашено</p>
          </div>
          <div className="glass-card p-5 text-center">
            <div className="p-2 rounded-lg bg-green-500/10 inline-flex mb-3">
              <Gift className="w-5 h-5 text-green-400" />
            </div>
            <p className="text-3xl font-bold text-amber-300">{info.total_earned.toLocaleString()}</p>
            <p className="text-xs text-gray-500 mt-1">Заработано токенов</p>
          </div>
          <div className="glass-card p-5 text-center">
            <div className="p-2 rounded-lg bg-blue-500/10 inline-flex mb-3">
              <UserPlus className="w-5 h-5 text-blue-400" />
            </div>
            <p className="text-3xl font-bold text-white">{info.total_referred > 0 ? (info.total_earned / info.total_referred).toFixed(0) : '0'}</p>
            <p className="text-xs text-gray-500 mt-1">Средний доход</p>
          </div>
        </div>
      )}

      {/* How it works */}
      <div className="glass-card p-6">
        <h3 className="text-sm font-semibold text-gray-200 mb-4">Как это работает</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { step: '01', label: 'Создайте код', desc: 'Сгенерируйте персональную реферальную ссылку' },
            { step: '02', label: 'Поделитесь', desc: 'Отправьте друзьям через соцсети или мессенджеры' },
            { step: '03', label: 'Получайте бонусы', desc: '500 токенов за каждого приглашённого друга' },
          ].map((s) => (
            <div key={s.step} className="flex items-start gap-3 p-3 rounded-xl glass-hover">
              <span className="text-2xl font-bold text-amber-500/30 w-8">{s.step}</span>
              <div>
                <p className="text-sm font-medium text-gray-200">{s.label}</p>
                <p className="text-xs text-gray-500">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Rewards history */}
      {rewards.length > 0 && (
        <div className="glass-card p-6">
          <h3 className="text-sm font-semibold text-gray-200 mb-4">История начислений</h3>
          <div className="space-y-2">
            {rewards.map((r) => (
              <div key={r.id} className="flex items-center justify-between p-3 rounded-xl glass-hover">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center">
                    <UserPlus className="w-4 h-4 text-green-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-200">{r.referred_name}</p>
                    <p className="text-xs text-gray-600">{r.referred_email}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-green-400">{r.amount.toLocaleString()}</p>
                  <p className="text-xs text-gray-600">{new Date(r.created_at).toLocaleDateString('ru-RU')}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
